import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { entities } from "@/api/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Eye, Loader2, ClipboardList } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import DetailsModal from "@/components/activitylog/DetailsModal";

const ACTION_LABELS = {
  edital_created: "Edital criado",
  edital_updated: "Edital atualizado",
  edital_deleted: "Edital excluído",
  edital_status_changed: "Status do edital alterado",
  contrato_created: "Contrato criado",
  contrato_updated: "Contrato atualizado",
  contrato_deleted: "Contrato excluído",
  contrato_status_changed: "Status do contrato alterado",
  proposta_created: "Proposta criada",
  proposta_updated: "Proposta atualizada",
  proposta_deleted: "Proposta excluída",
  event_created: "Evento criado",
  event_updated: "Evento atualizado",
  event_deleted: "Evento excluído",
  atestado_created: "Atestado criado",
  atestado_updated: "Atestado atualizado",
  atestado_deleted: "Atestado excluído",
  login_success: "Login realizado",
  login_failed: "Falha no login",
  kanban_column_moved: "Card movido (Kanban)",
  configuracao_empresa_updated: "Config. empresa atualizada",
};

const PAGE_SIZE = 50;

export default function ActivityLogPage() {
  const [search, setSearch] = useState("");
  const [actionType, setActionType] = useState("");
  const [entityType, setEntityType] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [offset, setOffset] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsEntry, setDetailsEntry] = useState(null);

  const params = {
    limit: PAGE_SIZE,
    offset,
    ...(search && { search }),
    ...(actionType && actionType !== "all" && { action_type: actionType }),
    ...(entityType && entityType !== "all" && { entity_type: entityType }),
    ...(status && status !== "all" && { status }),
    ...(dateFrom && { date_from: dateFrom }),
    ...(dateTo && { date_to: dateTo }),
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["activity-logs", params],
    queryFn: () => entities.ActivityLog.list(params),
  });

  const logs = data?.data ?? [];
  const total = data?.total ?? 0;
  const hasMore = offset + logs.length < total;
  const hasPrev = offset > 0;

  const handleClearFilters = () => {
    setSearch("");
    setActionType("");
    setEntityType("");
    setStatus("");
    setDateFrom("");
    setDateTo("");
    setOffset(0);
  };

  const actionLabel = (actionTypeKey) =>
    ACTION_LABELS[actionTypeKey] || actionTypeKey || "—";

  const openDetails = (log) => {
    setDetailsEntry({
      log,
      actionLabel: actionLabel(log.action_type),
    });
    setDetailsOpen(true);
  };

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2 text-xl">
              <ClipboardList className="w-6 h-6" />
              Log de Atividades
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-3 items-end mb-6">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Busca</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Usuário, entidade, descrição..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="w-[180px]">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Tipo de ação</label>
                <Select value={actionType || "all"} onValueChange={(v) => { setActionType(v === "all" ? "" : v); setOffset(0); }}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {Object.entries(ACTION_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[140px]">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Entidade</label>
                <Select value={entityType || "all"} onValueChange={(v) => { setEntityType(v === "all" ? "" : v); setOffset(0); }}>
                  <SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="Edital">Edital</SelectItem>
                    <SelectItem value="Contrato">Contrato</SelectItem>
                    <SelectItem value="Proposta">Proposta</SelectItem>
                    <SelectItem value="AgendaEvent">Evento</SelectItem>
                    <SelectItem value="AtestadoTecnico">Atestado Técnico</SelectItem>
                    <SelectItem value="ConfiguracaoEmpresa">Config. Empresa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[120px]">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Status</label>
                <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setOffset(0); }}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="success">Sucesso</SelectItem>
                    <SelectItem value="failed">Falha</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[150px]">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Data de</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }}
                />
              </div>
              <div className="w-[150px]">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Data até</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setOffset(0); }}
                />
              </div>
              <Button variant="outline" onClick={handleClearFilters}>
                Limpar filtros
              </Button>
            </div>

            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 text-sm">
                {error?.message || "Erro ao carregar o log. Tente novamente."}
              </div>
            )}

            {!isLoading && !error && (
              <>
                {logs.length === 0 ? (
                  <p className="text-center text-slate-500 py-12">
                    Nenhum registro encontrado para os filtros aplicados.
                  </p>
                ) : (
                  <div className="rounded-md border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data/Hora</TableHead>
                          <TableHead>Usuário</TableHead>
                          <TableHead>Tipo de ação</TableHead>
                          <TableHead>Entidade</TableHead>
                          <TableHead>Descrição</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-[80px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {logs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell className="whitespace-nowrap text-slate-600">
                              {log.timestamp
                                ? format(new Date(log.timestamp), "dd/MM/yyyy HH:mm", { locale: ptBR })
                                : "—"}
                            </TableCell>
                            <TableCell>
                              <span className="font-medium">{log.user_full_name || log.user_email || "—"}</span>
                              {log.user_full_name && log.user_email && (
                                <span className="text-xs text-slate-500 block">{log.user_email}</span>
                              )}
                            </TableCell>
                            <TableCell>{actionLabel(log.action_type)}</TableCell>
                            <TableCell>{log.entity_type || "—"}</TableCell>
                            <TableCell className="max-w-[220px] truncate" title={log.entity_name_description}>
                              {log.entity_name_description || "—"}
                            </TableCell>
                            <TableCell>
                              <Badge variant={log.status === "success" ? "default" : "destructive"}>
                                {log.status === "success" ? "Sucesso" : "Falha"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openDetails(log)}
                                title="Ver detalhes"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {total > 0 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-slate-600">
                      Exibindo {offset + 1}–{offset + logs.length} de {total}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!hasPrev}
                        onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!hasMore}
                        onClick={() => setOffset((o) => o + PAGE_SIZE)}
                      >
                        Próxima
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <DetailsModal
        open={detailsOpen}
        onOpenChange={(open) => {
          setDetailsOpen(open);
          if (!open) setDetailsEntry(null);
        }}
        log={detailsEntry?.log}
        actionLabel={detailsEntry?.actionLabel}
      />
    </div>
  );
}
